import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useLanguageStore } from "../store";
import { toast } from "react-toastify";
import { authService, subscriptionService } from "../services";
import { FiArrowLeft, FiCheckCircle } from "react-icons/fi";
import PaymentModal from "../components/PaymentModal";

// Planes de suscripción
const SubscriptionPlans = [
  {
    key: "free",
    name: "Cuenta gratuita",
    description: "Acceso básico y limitado a preguntas AI",
    price: "0€",
    features: [
      "Preguntas AI limitadas",
      "Gestión básica de entrevistas",
      "Sin estadísticas avanzadas",
    ],
  },
  {
    key: "premium",
    name: "Premium",
    description: "Acceso completo a todas las funciones",
    price: "9.99€/mes",
    features: [
      "Preguntas AI ilimitadas",
      "Estadísticas avanzadas",
      "Descarga de informes",
      "Prioridad de soporte",
    ],
  },
];

const Settings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguageStore();
  const [loading, setLoading] = useState(false);

  // Carga inicial desde localStorage solo si no hay usuario
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : {};
  });
  const [showPlans, setShowPlans] = useState(false);

  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    profession: user?.profession || "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  // Suscripción
  const subscription = user?.subscriptionStatus || "free";
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);

  // GET actual del usuario SIEMPRE desde backend al cargar Settings
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await authService.getMe(); // GET /auth/me
        if (res && res.data && res.data.user) {
          setUser(res.data.user);
          localStorage.setItem("user", JSON.stringify(res.data.user));
          setProfileData({
            firstName: res.data.user?.firstName || "",
            lastName: res.data.user?.lastName || "",
            profession: res.data.user?.profession || "",
          });

          // Fetch subscription data to get daysRemaining
          try {
            const subRes = await subscriptionService.getSubscription();
            if (subRes && subRes.data && subRes.data.subscription) {
              setSubscriptionData(subRes.data.subscription);
            }
          } catch (subError) {
            console.error("Error fetching subscription:", subError);
            // No mostrar error al usuario, solo log
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        // Si hay un error 404, el endpoint no existe o el usuario no está autenticado
        if (error.response && error.response.status === 404) {
          toast.error(
            "No se pudo cargar el perfil. Por favor, inicia sesión nuevamente."
          );
          // No redirigir aquí, el interceptor de axios ya lo hace
        } else if (error.response && error.response.status === 401) {
          // El interceptor de axios manejará esto
          console.log("Usuario no autenticado");
        } else {
          // Usar datos locales como fallback
          console.log("Usando datos de usuario del localStorage como fallback");
        }
      }
    };
    fetchProfile();
  }, []);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    if (!profileData.firstName.trim() || !profileData.lastName.trim()) {
      toast.warning("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      const response = await authService.updateProfile(profileData);
      const updatedUser = {
        ...user,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        profession: profileData.profession,
        ...response.data,
      };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      window.dispatchEvent(new Event("userUpdated"));
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error updating profile";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (
      !passwordData.currentPassword.trim() ||
      !passwordData.newPassword.trim()
    ) {
      toast.warning("Please fill in all fields");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      toast.error(t("auth.passwordMismatch") || "Passwords do not match");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.warning("Password must be at least 6 characters long");
      return;
    }
    setLoading(true);
    try {
      await authService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
      toast.success("Password changed successfully!");
    } catch (error) {
      console.error("Error changing password:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error changing password";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    const updatedUser = { ...user, language: lang };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    toast.success(`Language changed to ${lang}`);
  };

  // Upgrade usuario
  const handleUpgrade = async () => {
    try {
      setIsProcessing(true);

      // Create simulated payment
      const paymentResponse = await subscriptionService.createSimulatedPayment({
        plan: "premium",
        billingCycle: "monthly",
      });

      if (!paymentResponse.data.success) {
        toast.error("Failed to initiate payment");
        setIsProcessing(false);
        return;
      }

      // Store payment data and show modal
      setPaymentData({
        paymentId: paymentResponse.data.paymentId,
        amount: paymentResponse.data.amount,
      });
      setShowPaymentModal(true);
      setIsProcessing(false);
    } catch (error) {
      console.error("Upgrade error:", error);
      toast.error(error.response?.data?.message || "Error processing payment");
      setIsProcessing(false);
    }
  };

  const handlePaymentSubmit = async (cardData) => {
    try {
      setIsProcessing(true);

      // Execute simulated payment
      const executeResponse = await subscriptionService.executeSimulatedPayment(
        {
          paymentId: paymentData.paymentId,
          plan: "premium",
          billingCycle: "monthly",
          cardData: cardData,
        }
      );

      if (executeResponse.data.success) {
        toast.success("¡Pago realizado correctamente! Bienvenido a Premium");
        setShowPaymentModal(false);
        setPaymentData(null);

        // Refresh user data
        const userRes = await authService.getMe();
        setUser(userRes.data.user);
        localStorage.setItem("user", JSON.stringify(userRes.data.user));
        setShowPlans(false);
      } else {
        toast.error(executeResponse.data.message || "Pago fallido");
      }
    } catch (error) {
      console.error("Payment execution error:", error);

      // Simulate occasional payment failures
      if (error.response?.status === 402) {
        toast.error("Fondos insuficientes. Intenta con otra tarjeta.");
      } else {
        toast.error(
          error.response?.data?.message || "Error al procesar el pago"
        );
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (
      window.confirm(
        "¿Estás seguro de que quieres cancelar tu suscripción Premium?"
      )
    ) {
      try {
        setIsProcessing(true);
        await subscriptionService.cancelSubscription();
        toast.success("Suscripción cancelada correctamente");

        // Refresh user data
        const userRes = await authService.getMe();
        setUser(userRes.data.user);
        localStorage.setItem("user", JSON.stringify(userRes.data.user));
        setShowPlans(false);
      } catch (error) {
        console.error("Cancel error:", error);
        toast.error(
          error.response?.data?.message || "Error al cancelar la suscripción"
        );
      } finally {
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-wrapper">
        <div className="settings-header">
          <button
            onClick={() => navigate("/dashboard")}
            className="settings-back-button"
          >
            <FiArrowLeft /> {t("Volver") || "Back"}
          </button>
          <h1 className="settings-title">
            {t("settings.title") || "Settings"}
          </h1>
        </div>

        {/* SUSCRIPCIÓN */}
        <div className="settings-section">
          <h2 className="settings-section-title">Suscripción</h2>
          <div className="subscription-header">
            <div className={`subscription-badge ${subscription}`}>
              <FiCheckCircle />
              <span>{subscription === "premium" ? "Premium" : "Gratis"}</span>
              {subscriptionData?.status === "cancelled" &&
                subscriptionData?.daysRemaining > 0 && (
                  <span className="subscription-days-remaining">
                    ({subscriptionData.daysRemaining} días restantes)
                  </span>
                )}
            </div>
            <button
              onClick={() => setShowPlans(!showPlans)}
              className="btn btn-primary"
            >
              Gestionar suscripción
            </button>
          </div>

          {/* Mensaje de cancelación programada */}
          {subscriptionData?.status === "cancelled" &&
            subscriptionData?.daysRemaining > 0 && (
              <div className="alert alert-warning">
                <FiCheckCircle className="alert-icon" />
                <div className="alert-content">
                  <p className="alert-title">Tu suscripción fue cancelada</p>
                  <p className="alert-message">
                    Seguirás disfrutando de Premium hasta el{" "}
                    <strong>
                      {new Date(
                        subscriptionData.finalEndDate
                      ).toLocaleDateString("es-ES")}
                    </strong>
                    ({subscriptionData.daysRemaining} días). Después, tu cuenta
                    volverá al plan gratuito.
                  </p>
                </div>
              </div>
            )}

          {showPlans && (
            <div className="settings-plans-grid">
              {SubscriptionPlans.map((plan) => (
                <div
                  key={plan.key}
                  className={`plan-card ${
                    subscription === plan.key ? "current" : ""
                  }`}
                >
                  <div className="plan-header">
                    <h3 className="plan-name">
                      {plan.name}
                      {subscription === plan.key && (
                        <span className="badge badge-primary ml-2">Actual</span>
                      )}
                    </h3>
                    <p className="plan-period">{plan.description}</p>
                    <div className="plan-price">{plan.price}</div>
                  </div>
                  <div className="plan-body">
                    <ul className="plan-features">
                      {plan.features.map((feat) => (
                        <li key={feat} className="plan-feature">
                          <FiCheckCircle className="plan-feature-icon included" />
                          <span className="plan-feature-text">{feat}</span>
                        </li>
                      ))}
                    </ul>
                    {subscription !== plan.key && plan.key === "premium" && (
                      <button
                        onClick={handleUpgrade}
                        disabled={isProcessing}
                        className="btn btn-primary"
                      >
                        {isProcessing
                          ? "Procesando..."
                          : "Actualizar a Premium"}
                      </button>
                    )}
                    {subscription === plan.key &&
                      plan.key === "premium" &&
                      subscriptionData?.status !== "cancelled" && (
                        <button
                          onClick={handleCancelSubscription}
                          disabled={isProcessing}
                          className="btn btn-danger"
                        >
                          {isProcessing
                            ? "Cancelando..."
                            : "Cancelar Suscripción"}
                        </button>
                      )}
                    {subscription === plan.key &&
                      plan.key === "premium" &&
                      subscriptionData?.status === "cancelled" && (
                        <div className="subscription-cancelled-box">
                          <p className="subscription-cancelled-text">
                            Cancelada el{" "}
                            {new Date(
                              subscriptionData.cancellationDate
                            ).toLocaleDateString("es-ES")}
                          </p>
                        </div>
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="settings-grid-2">
          {/* Perfil */}
          <div className="settings-section">
            <h2 className="settings-section-title">
              {t("settings.profile") || "Profile Settings"}
            </h2>
            <form onSubmit={handleUpdateProfile} className="settings-form">
              <div className="settings-form-group">
                <label className="settings-form-label">
                  {t("common.firstName") || "First Name"}
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={profileData.firstName}
                  onChange={handleProfileChange}
                  className="settings-form-input"
                  required
                />
              </div>
              <div className="settings-form-group">
                <label className="settings-form-label">
                  {t("common.lastName") || "Last Name"}
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={profileData.lastName}
                  onChange={handleProfileChange}
                  className="settings-form-input"
                  required
                />
              </div>
              <div className="settings-form-group">
                <label className="settings-form-label">
                  {t("common.profession") || "Profession"}
                </label>
                <input
                  type="text"
                  name="profession"
                  value={profileData.profession}
                  onChange={handleProfileChange}
                  className="settings-form-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="settings-save-button"
              >
                {loading ? (
                  <>
                    <div className="spinner-small"></div>
                    {t("common.loading") || "Loading..."}
                  </>
                ) : (
                  t("settings.updateProfile") || "Update Profile"
                )}
              </button>
            </form>
          </div>
          {/* Contraseña */}
          <div className="settings-section">
            <h2 className="settings-section-title">
              {t("settings.security") || "Security"}
            </h2>
            <form onSubmit={handleChangePassword} className="settings-form">
              <div className="settings-form-group">
                <label className="settings-form-label">
                  {t("settings.currentPassword") || "Current Password"}
                </label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className="settings-form-input"
                  required
                />
              </div>
              <div className="settings-form-group">
                <label className="settings-form-label">
                  {t("settings.newPassword") || "New Password"}
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className="settings-form-input"
                  required
                />
              </div>
              <div className="settings-form-group">
                <label className="settings-form-label">
                  {t("settings.confirmNewPassword") || "Confirm New Password"}
                </label>
                <input
                  type="password"
                  name="confirmNewPassword"
                  value={passwordData.confirmNewPassword}
                  onChange={handlePasswordChange}
                  className="settings-form-input"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="settings-save-button"
              >
                {loading ? (
                  <>
                    <div className="spinner-small"></div>
                    {t("common.loading") || "Loading..."}
                  </>
                ) : (
                  t("settings.changePassword") || "Change Password"
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Preferencias */}
        <div className="settings-section">
          <h2 className="settings-section-title">
            {t("settings.preferences") || "Preferences"}
          </h2>
          <div className="settings-form-group settings-form-group-narrow">
            <label className="settings-form-label">
              {t("settings.language") || "Language"}
            </label>
            <select
              value={language}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="settings-form-select"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
        </div>

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentData(null);
          }}
          onConfirm={handlePaymentSubmit}
          amount={paymentData?.amount}
          isProcessing={isProcessing}
        />
      </div>
    </div>
  );
};

export default Settings;
